import {
  summarizeRealized,
  valuateRealized,
  type FxRate,
  type RealizedEntry,
  type ValidatedDividend,
} from '@porttang/core';
import { useMemo, useState } from 'react';
import {
  EM_DASH,
  formatDate,
  formatPercent,
  formatPrice,
  formatQuantity,
  formatSignedAmount,
  formatSignedTHB,
  toneClassFor,
} from '../../shared/format';
import { AssetTag, Card, SectionLabel } from '../../shared/pixel';
import { useHoldings } from '../holdings/useHoldings';
import { useFxRates } from '../prices/usePrices';
import { DividendForm } from './DividendForm';
import { useCreateDividend, useDeleteRealized, useRealized } from './useRealized';

const EMPTY_ENTRIES: readonly RealizedEntry[] = [];
const EMPTY_RATES: ReadonlyMap<string, FxRate> = new Map();

const ADD_BUTTON_CLASS =
  'rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink-100 transition hover:brightness-110 disabled:opacity-50';

/** ผลตอบแทนที่รับรู้แล้ว — กำไรจากการขาย + ปันผล (spec Section 9.6) */
export function RealizedPage() {
  const realizedQuery = useRealized();
  const holdingsQuery = useHoldings();
  const fxQuery = useFxRates();
  const createDividend = useCreateDividend();
  const deleteEntry = useDeleteRealized();

  const [isAddingDividend, setAddingDividend] = useState(false);

  const entries = realizedQuery.data ?? EMPTY_ENTRIES;
  // ยังโหลด FX ไม่เสร็จ = ยังไม่รู้ ไม่ใช่ "หา FX ไม่เจอ" — แสดง — แทนการเตือนว่ายอดไม่ครบ
  const rates: ReadonlyMap<string, FxRate> | null =
    fxQuery.data ?? (fxQuery.isError ? EMPTY_RATES : null);

  const totals = useMemo(
    () => (rates === null ? null : summarizeRealized(entries, rates)),
    [entries, rates],
  );
  const valuations = useMemo(
    () =>
      rates === null
        ? null
        : new Map(entries.map((entry) => [entry.id, valuateRealized(entry, rates)])),
    [entries, rates],
  );

  const dividendHoldings = useMemo(
    () => (holdingsQuery.data ?? []).filter((holding) => holding.assetType !== 'cash'),
    [holdingsQuery.data],
  );

  const writeError = createDividend.error ?? deleteEntry.error;

  function handleAddDividend(dividend: ValidatedDividend) {
    createDividend.mutate(dividend, { onSuccess: () => setAddingDividend(false) });
  }

  function handleDelete(entry: RealizedEntry) {
    const message =
      entry.kind === 'sale'
        ? `ลบรายการขาย ${entry.symbol}?\n\nจำนวนหน่วยจะไม่ถูกคืนเข้าพอร์ต ถ้าบันทึกขายผิด ต้องแก้จำนวนที่หน้าสินทรัพย์เองด้วย`
        : `ลบปันผล ${entry.symbol}?`;
    if (globalThis.confirm(message)) {
      deleteEntry.mutate(entry.id);
    }
  }

  return (
    <section className="mx-auto w-full max-w-[980px]">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <SectionLabel>REALIZED RETURNS</SectionLabel>
          <h1 className="mt-2 text-xl font-semibold text-ink-100">ขาย &amp; ปันผล</h1>
          <p className="mt-1 text-sm text-ink-500">
            กำไรที่ทำได้จริงจากการขาย และปันผลที่ได้รับ — ยังนับอยู่แม้ขายหมดไปแล้ว
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAddingDividend(true)}
          disabled={dividendHoldings.length === 0}
          title={dividendHoldings.length === 0 ? 'ต้องมีสินทรัพย์ในพอร์ตก่อน' : undefined}
          className={ADD_BUTTON_CLASS}
        >
          + บันทึกปันผล
        </button>
      </header>

      {writeError !== null && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-down/25 bg-down/10 px-4 py-3 text-sm text-down"
        >
          บันทึกไม่สำเร็จ: {writeError.message}
        </p>
      )}

      {realizedQuery.isPending && (
        <p className="py-12 text-center font-mono text-sm text-ink-500">กำลังโหลด…</p>
      )}

      {realizedQuery.isError && (
        <div className="rounded-[14px] border border-down/30 bg-down/5 px-6 py-10 text-center">
          <p className="text-sm text-down">โหลดข้อมูลไม่สำเร็จ: {realizedQuery.error.message}</p>
          <button
            type="button"
            onClick={() => void realizedQuery.refetch()}
            className="mt-4 rounded-md border border-line-strong px-4 py-2 text-sm text-ink-200 transition hover:border-brand hover:text-brand-text"
          >
            ลองใหม่
          </button>
        </div>
      )}

      {realizedQuery.isSuccess && entries.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-line-dash px-6 py-16 text-center">
          <SectionLabel>NOTHING REALIZED YET</SectionLabel>
          <p className="mt-4 text-ink-200">ยังไม่มีรายการขายหรือปันผล</p>
          <p className="mt-1.5 text-sm text-ink-500">
            กด “ขาย” ที่หน้าสินทรัพย์ ระบบจะเก็บกำไรไว้ที่นี่ — หรือบันทึกปันผลที่ได้รับ
          </p>
        </div>
      )}

      {realizedQuery.isSuccess && entries.length > 0 && (
        <div className="flex flex-col gap-5">
          <Card className="p-5">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-ink-400">กำไรจากการขาย · {totals?.saleCount ?? 0}</dt>
                <dd className={`mt-1 font-mono text-[19px] ${toneClassFor(totals?.salePnlTHB)}`}>
                  {formatSignedTHB(totals?.salePnlTHB)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-ink-400">ปันผลสะสม · {totals?.dividendCount ?? 0}</dt>
                <dd className={`mt-1 font-mono text-[19px] ${toneClassFor(totals?.dividendsTHB)}`}>
                  {formatSignedTHB(totals?.dividendsTHB)}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <dt className="text-xs text-ink-400">รวมที่รับรู้แล้ว</dt>
                <dd className={`mt-1 font-mono text-[19px] ${toneClassFor(totals?.totalTHB)}`}>
                  {formatSignedTHB(totals?.totalTHB)}
                </dd>
              </div>
            </dl>

            {totals?.isPartial === true && (
              <p className="mt-4 rounded-lg border border-warn/25 bg-warn/10 px-3.5 py-2.5 text-xs leading-relaxed text-warn">
                ยอดรวมยังไม่ครบ — มี {totals.incompleteCount} รายการที่ยังแปลงเป็นบาทไม่ได้
                เพราะไม่มีอัตราแลกเปลี่ยน
              </p>
            )}
            <p className="mt-3 text-[11px] text-ink-600">
              แปลงเป็นบาทด้วยอัตราแลกเปลี่ยนปัจจุบัน เหมือนตัวเลขอื่นทั้งแอป
            </p>
          </Card>

          <Card className="overflow-hidden">
            <ul className="divide-y divide-line-soft">
              {entries.map((entry) => {
                const valuation = valuations?.get(entry.id) ?? null;
                return (
                  <li key={entry.id} className="flex items-start gap-3 px-4 py-3.5 sm:px-5">
                    <AssetTag symbol={entry.symbol} />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-ink-100">{entry.symbol}</span>
                        <KindBadge kind={entry.kind} />
                      </div>
                      <div className="truncate font-mono text-[11px] text-ink-500">
                        {entry.platform} ·{' '}
                        {entry.recordedAt === null ? 'เพิ่งบันทึก' : formatDate(entry.recordedAt)}
                      </div>
                      <div className="mt-1 text-[12px] text-ink-400">
                        {entry.kind === 'sale' ? (
                          <>
                            ขาย {formatQuantity(entry.quantity)} ×{' '}
                            {formatPrice(entry.price, entry.currency)} · ทุน{' '}
                            {formatPrice(entry.avgCost, entry.currency)}
                          </>
                        ) : (
                          <>
                            ได้รับ {formatPrice(entry.amount, entry.currency)}
                            {entry.note !== null && ` · ${entry.note}`}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      <div
                        className={`font-mono text-[14px] font-semibold ${toneClassFor(valuation?.amount)}`}
                      >
                        {valuation === null ? EM_DASH : formatSignedTHB(valuation.amountTHB)}
                      </div>
                      {valuation !== null && (
                        <div className="font-mono text-[10.5px] text-ink-500">
                          {formatSignedAmount(valuation.amount, entry.currency)}
                          {valuation.pnlPct !== null && ` · ${formatPercent(valuation.pnlPct)}`}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(entry)}
                        disabled={deleteEntry.isPending}
                        className="mt-1.5 rounded border border-line-strong px-2 py-1 text-[11px] text-down transition hover:bg-down/10 disabled:opacity-50"
                      >
                        ลบ
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      )}

      {isAddingDividend && dividendHoldings.length > 0 && (
        <DividendForm
          holdings={dividendHoldings}
          isSaving={createDividend.isPending}
          onCancel={() => setAddingDividend(false)}
          onSubmit={handleAddDividend}
        />
      )}
    </section>
  );
}

function KindBadge({ kind }: { kind: RealizedEntry['kind'] }) {
  return kind === 'sale' ? (
    <span className="rounded bg-brand-soft px-1.5 py-0.5 text-[10px] text-brand-text">ขาย</span>
  ) : (
    <span className="rounded bg-up/12 px-1.5 py-0.5 text-[10px] text-up">ปันผล</span>
  );
}
